import React, { useState, useEffect } from 'react';
import { aiAPI } from '../../../services/api';

interface SynthesisEntry {
  id: string;
  date: string;
  content: string;
  created_time: string | null;
}

export const DailySynthesisViewer: React.FC = () => {
  const [syntheses, setSyntheses] = useState<SynthesisEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await aiAPI.getDailySyntheses(30);
        if (!cancelled) setSyntheses(result.syntheses);
      } catch {
        if (!cancelled) setError('Failed to load daily syntheses');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Daily Synthesis History
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Archived daily snapshots of the agent's rolling memory. Each entry captures what the agent knew at the end of that day.
      </p>

      {error && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center">
          Loading...
        </div>
      ) : syntheses.length === 0 ? (
        <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center italic">
          No daily syntheses yet.
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
          {syntheses.map((s, i) => (
            <div
              key={s.id}
              className={i > 0 ? 'border-t border-gray-200 dark:border-gray-600' : ''}
            >
              <button
                onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                className="w-full px-3 py-2 flex items-center justify-between text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {formatDate(s.date)}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${expandedId === s.id ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedId === s.id && (
                <div className="px-3 pb-3">
                  <div className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-64 overflow-y-auto">
                    {s.content || 'No content.'}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
